import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';

import { getRiderFilterOptions } from '../../api/services/Riders';
import { useAuthQuery } from '../../auth/hooks/useAuthQuery';
import { useInvoiceGroups } from '../../organizers/hooks/useInvoiceGroups';
import { FilterSection } from '../../shared/components/FilterDrawer';
import type { RidersQueryParams } from '../types';

const INVOICE_GROUPS_PER_PAGE = 10;

export const useRiderFilters = (
  isDrawerOpen = false,
  ridersQuery: {
    queryParams: RidersQueryParams;
    setActiveFilters: (selections: Record<string, string[]>) => void;
    clearAllFilters: () => void;
  }
) => {
  const { queryParams, setActiveFilters, clearAllFilters } = ridersQuery;
  const { isAdminOrOrganizerUser } = useAuthQuery();
  const [filterOptions, setFilterOptions] = useState<RiderFilterOptions | null>(null);

  const { invoiceGroups, totalCount, isFetching, hasFetched, fetchInvoiceGroups } = useInvoiceGroups();

  const [igIsMultiPage, setIgIsMultiPage] = useState(false);
  const hasSetMultiPage = useRef(false);
  const igCurrentPage = useRef(1);
  const igSearchQueryRef = useRef('');
  const prevDrawerOpenRef = useRef(false);

  useEffect(() => {
    const controller = new AbortController();

    getRiderFilterOptions(controller.signal).then((response) => {
      if (response?.type === 'success') {
        setFilterOptions(response.data.filterOptions);
      }
    });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (hasFetched && !hasSetMultiPage.current) {
      hasSetMultiPage.current = true;
      setIgIsMultiPage(totalCount > INVOICE_GROUPS_PER_PAGE);
    }
  }, [hasFetched, totalCount]);

  useEffect(() => {
    const wasOpen = prevDrawerOpenRef.current;

    prevDrawerOpenRef.current = isDrawerOpen;

    if (isDrawerOpen && !hasFetched && isAdminOrOrganizerUser) {
      igCurrentPage.current = 1;
      fetchInvoiceGroups({ page: 1, perPage: INVOICE_GROUPS_PER_PAGE });
    }

    // Drawer just closed after a search — reset to full list so it's ready on next open
    if (wasOpen && !isDrawerOpen && igSearchQueryRef.current) {
      igSearchQueryRef.current = '';
      igCurrentPage.current = 1;
      fetchInvoiceGroups({ page: 1, perPage: INVOICE_GROUPS_PER_PAGE });
    }
  }, [isDrawerOpen, hasFetched, isAdminOrOrganizerUser, fetchInvoiceGroups]);

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        igCurrentPage.current = 1;
        fetchInvoiceGroups({ page: 1, perPage: INVOICE_GROUPS_PER_PAGE, ...(query && { q: query }) });
      }, 300),
    [fetchInvoiceGroups]
  );

  const handleIgSearch = useCallback(
    (query: string) => {
      igSearchQueryRef.current = query;
      debouncedSearch(query);
    },
    [debouncedSearch]
  );

  const handleIgLoadMore = useCallback(() => {
    const nextPage = igCurrentPage.current + 1;

    igCurrentPage.current = nextPage;
    fetchInvoiceGroups({
      page: nextPage,
      perPage: INVOICE_GROUPS_PER_PAGE,
      ...(igSearchQueryRef.current && { q: igSearchQueryRef.current }),
    });
  }, [fetchInvoiceGroups]);

  const igOptions = invoiceGroups.map((g) => ({ label: g.name, value: String(g.id) }));
  const igHasMore = invoiceGroups.length < totalCount;
  const igSelected = queryParams.invoiceGroupIds ? queryParams.invoiceGroupIds.split(',').filter(Boolean) : [];

  const isLoadingFilterOptions = filterOptions === null;

  const sections: FilterSection[] = [
    {
      key: 'safetySettings',
      label: 'Safety Settings',
      options: filterOptions?.safetySettings ?? [],
      selected: queryParams.safetySettings ?? [],
      isLoading: isLoadingFilterOptions,
    },
    {
      key: 'categories',
      label: 'Category',
      options: filterOptions?.categories ?? [],
      selected: queryParams.categories ?? [],
      isLoading: isLoadingFilterOptions,
    },
    ...(isAdminOrOrganizerUser
      ? [
          {
            key: 'invoiceGroups',
            label: 'Invoice Group',
            options: igOptions,
            selected: igSelected,
            searchable: igIsMultiPage,
            searchPlaceholder: 'Search invoice groups',
            onSearch: handleIgSearch,
            isSearching: isFetching,
            hasMore: igHasMore,
            onLoadMore: handleIgLoadMore,
            isLoading: isFetching && !hasFetched,
          },
        ]
      : []),
    {
      key: 'services',
      label: 'Services',
      options: (filterOptions?.services ?? []).map((s) => ({ label: s.name, value: String(s.id) })),
      selected: queryParams.services ?? [],
      isLoading: isLoadingFilterOptions,
      emptyMessage: 'No services enabled',
    },
  ];

  const activeFilterCount =
    (queryParams.safetySettings?.length ?? 0) +
    (queryParams.categories?.length ?? 0) +
    (queryParams.services?.length ?? 0) +
    igSelected.length;

  return {
    sections,
    filterOptions,
    activeFilterCount,
    hasActiveFilters: activeFilterCount > 0,
    setActiveFilters,
    clearAllFilters,
  };
};
